import React, { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { Box } from '@mui/material';
import { IUserMenuCategory } from '../../pages/CategoriesManagement/categoriesManagement.types';

type DragItem = {
  index: number;
  id: string;
  type: string;
};

const ItemType = 'CATEGORY';

interface CategoryItemProps {
  category: IUserMenuCategory;
  index: number;
  moveCategory: (dragIndex: number, hoverIndex: number) => void;
  children: React.ReactNode;
}

const CategoryItem: React.FC<CategoryItemProps> = ({ category, index, moveCategory, children }) => {
  const ref = useRef<HTMLDivElement>(null);

  const [, drop] = useDrop<DragItem>({
    accept: ItemType,
    hover(item, monitor) {
      if (!ref.current) return;

      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) return;

      const hoverBoundingRect = ref.current.getBoundingClientRect();

      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;

      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      moveCategory(dragIndex, hoverIndex);

      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: ItemType,
    item: { id: category.id, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));

  return (
    <Box ref={ref} style={{ opacity: isDragging ? 0.5 : 1, cursor: 'move', width: '100%' }}>
      {children}
    </Box>
  );
};

export default CategoryItem;
